const WORDPRESS_ORIGIN =
  import.meta.env.VITE_WORDPRESS_ORIGIN || "https://travel.rethinkways.com";
const API_PROXY_WPJSON_BASE = "/jnews-api/wp-json";
const WPJSON_BASE = import.meta.env.DEV
  ? API_PROXY_WPJSON_BASE
  : `${WORDPRESS_ORIGIN}/wp-json`;
const API_BASE_PATH = `${WPJSON_BASE}/wp/v2`;
const WP_API_BASE = `${API_BASE_PATH}/posts`;
const WP_CATEGORIES_API = `${API_BASE_PATH}/categories`;
const WP_TAGS_API = `${API_BASE_PATH}/tags`;
const TRW_SEO_API_BASE = `${WPJSON_BASE}/trw/v1`;
const DEFAULT_CATEGORY_SLUG = "brand-story";
const LATEST_UPDATES_SLUG = "latest-updates";
// Keep archive/category requests lightweight for fast first paint.
// Keep archive/category requests lightweight for fast first paint.
// (WP REST API caps `per_page` at 100; higher values are rejected.)
const POSTS_PER_PAGE = 24;
const REQUEST_CACHE_TTL_MS = 5 * 60 * 1000;
const requestCache = new Map();

const DEFAULT_FETCH_PARAMS = {
  per_page: POSTS_PER_PAGE,
  _embed: "true",
  // `context=embed` keeps payload smaller while still including enough data
  // for archive cards (including featured media in many WP setups).
  context: "embed",
};

async function fetchJson(url, { cacheKey = url, ttlMs = REQUEST_CACHE_TTL_MS } = {}) {
  const now = Date.now();
  const cachedEntry = requestCache.get(cacheKey);

  if (cachedEntry) {
    if (cachedEntry.data && now - cachedEntry.timestamp < ttlMs) {
      return cachedEntry.data;
    }

    if (cachedEntry.promise) {
      return cachedEntry.promise;
    }
  }

  const request = fetch(url).then(async (response) => {
    if (!response.ok) {
      const error = new Error(`Request failed with status ${response.status}`);
      error.status = response.status;
      error.url = url;
      throw error;
    }

    const data = await response.json();

    requestCache.set(cacheKey, {
      data,
      timestamp: Date.now(),
    });

    return data;
  });

  requestCache.set(cacheKey, {
    promise: request,
    timestamp: now,
  });

  try {
    return await request;
  } catch (error) {
    requestCache.delete(cacheKey);
    throw error;
  }
}

export async function fetchPosts(overrides = {}) {
  const query = new URLSearchParams({
    ...DEFAULT_FETCH_PARAMS,
    ...overrides,
  }).toString();
  const url = `${WP_API_BASE}?${query}`;

  return fetchJson(url);
}

export async function fetchPostsByIds(ids = []) {
  const normalizedIds = ids
    .map((id) => String(id).trim())
    .filter(Boolean);

  if (!normalizedIds.length) {
    return [];
  }

  return fetchPosts({
    include: normalizedIds.join(","),
    per_page: normalizedIds.length,
  });
}

export async function fetchTagsBySearch(query, { per_page = 20 } = {}) {
  const normalized = String(query || "").trim();
  if (!normalized) return [];

  const url = `${WP_TAGS_API}?${new URLSearchParams({
    search: normalized,
    per_page: String(per_page),
    _fields: "id,name,slug",
  }).toString()}`;

  const tags = await fetchJson(url, { cacheKey: `tags:${normalized}` });
  return Array.isArray(tags) ? tags : [];
}

export function getCategorySlugFromPath(
  pathname = typeof window !== "undefined" ? window.location.pathname : "/"
) {
  const match = pathname.match(/\/category\/([^/]+)\/?/i);
  return match?.[1] || DEFAULT_CATEGORY_SLUG;
}

export function isLatestUpdatesPath(
  pathname = typeof window !== "undefined" ? window.location.pathname : "/"
) {
  return /\/latest-updates\/?/i.test(pathname);
}

export async function fetchCategoryBySlug(slug) {
  const categories = await fetchJson(
    `${WP_CATEGORIES_API}?slug=${encodeURIComponent(slug)}&_fields=id,name,slug,link,yoast_head_json,rank_math_head`,
    { cacheKey: `category:${slug}` }
  );

  return categories[0] || null;
}

async function fetchCategoryBySlugFull(slug) {
  const categories = await fetchJson(
    `${WP_CATEGORIES_API}?slug=${encodeURIComponent(slug)}`,
    { cacheKey: `category_full:${slug}` }
  );

  return categories[0] || null;
}

export async function fetchCategorySeoBySlug(slug) {
  let category = await fetchCategoryBySlug(slug);

  if (!category) {
    return null;
  }

  if (category.rank_math_head == null && category.yoast_head_json == null) {
    const full = await fetchCategoryBySlugFull(slug);
    if (full) category = full;
  }

  const seo = category.yoast_head_json || null;

  return {
    category,
    title: seo?.title || null,
    description: seo?.og_description || seo?.description || null,
    canonicalUrl: seo?.canonical || category.link || null,
    image:
      seo?.og_image?.[0]?.url ||
      seo?.twitter_image ||
      seo?.schema?.["@graph"]?.find((entry) => entry?.primaryImageOfPage?.url)
        ?.primaryImageOfPage?.url ||
      null,
  };
}

export async function fetchRankMathTermSeo({ taxonomy = "category", slug, id } = {}) {
  const params = new URLSearchParams();
  params.set("taxonomy", taxonomy);
  if (id != null && String(id).trim() !== "") params.set("id", String(id));
  if (slug != null && String(slug).trim() !== "") params.set("slug", String(slug));

  return fetchJson(`${TRW_SEO_API_BASE}/term-seo?${params.toString()}`, {
    cacheKey: `rankmath_term_seo:${taxonomy}:${id ?? ""}:${slug ?? ""}`,
  });
}

export async function fetchCategoryBundleBySlug(slug, { per_page = POSTS_PER_PAGE } = {}) {
  return fetchJson(
    `${TRW_SEO_API_BASE}/category-bundle?slug=${encodeURIComponent(slug)}&per_page=${encodeURIComponent(per_page)}`,
    { cacheKey: `category_bundle:${slug}:${per_page}` }
  );
}

export async function fetchPostsForCategorySlug(slug, overrides = {}) {
  const category = await fetchCategoryBySlug(slug);

  if (!category) {
    throw new Error(`No category found for slug "${slug}"`);
  }

  return fetchPosts({
    categories: category.id,
    ...overrides,
  });
}

export async function fetchCategoryArchiveBySlug(slug, overrides = {}) {
  const category = await fetchCategoryBySlug(slug);

  if (!category) {
    throw new Error(`No category found for slug "${slug}"`);
  }

  const posts = await fetchPosts({
    categories: category.id,
    ...overrides,
  });

  return { category, posts };
}

export {
  API_BASE_PATH,
  DEFAULT_CATEGORY_SLUG,
  DEFAULT_FETCH_PARAMS,
  LATEST_UPDATES_SLUG,
  POSTS_PER_PAGE,
  WP_API_BASE,
  WP_CATEGORIES_API,
  WP_TAGS_API,
};
